import { createContext, useContext, useState, useCallback, ReactNode, useRef } from 'react';
import { Product, ProductUpdateData } from '@/services/products.service';

interface ProductEditContextType {
  // Legacy API (kept for backward compatibility)
  hasChanges: boolean;
  setHasChanges: (value: boolean) => void;
  markAsChanged: () => void;
  resetChanges: () => void;
  
  // New change tracking API
  productChanges: Partial<Product>;
  originalProduct: Product | null;
  updateField: <K extends keyof Product>(field: K, value: Product[K]) => void;
  updateNestedField: (field: keyof Product, nestedField: string, value: unknown) => void;
  getUpdatedProduct: () => ProductUpdateData;
  isFieldChanged: (field: keyof Product) => boolean;
  isSaving: boolean;
  setSaving: (saving: boolean) => void;
  initializeProduct: (product: Product) => void;
}

const ProductEditContext = createContext<ProductEditContextType | undefined>(undefined);

export function ProductEditProvider({ children }: { children: ReactNode }) {
  const [hasChanges, setHasChanges] = useState(false);
  const [productChanges, setProductChanges] = useState<Partial<Product>>({});
  const [originalProduct, setOriginalProduct] = useState<Product | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const originalProductRef = useRef<Product | null>(null);

  const markAsChanged = useCallback(() => {
    setHasChanges(true);
  }, []);

  const resetChanges = useCallback(() => {
    setHasChanges(false);
    setProductChanges({});
    setIsSaving(false);
  }, []);

  const initializeProduct = useCallback((product: Product) => {
    setOriginalProduct(product);
    originalProductRef.current = product;
    setProductChanges({});
    setHasChanges(false);
  }, []);

  const updateField = useCallback(<K extends keyof Product>(field: K, value: Product[K]) => {
    setProductChanges(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  }, []);

  const updateNestedField = useCallback((field: keyof Product, nestedField: string, value: unknown) => {
    setProductChanges(prev => {
      const currentFieldValue = prev[field] || originalProductRef.current?.[field] || {};
      return {
        ...prev,
        [field]: {
          ...(typeof currentFieldValue === 'object' ? currentFieldValue : {}),
          [nestedField]: value
        }
      };
    });
    setHasChanges(true);
  }, []);

  const getUpdatedProduct = useCallback((): ProductUpdateData => {
    // Filter out read-only fields and undefined values
    const readOnlyFields = ['id', 'endpoint_count', 'customer_count', 'health_score', 'usage_statistics', 'usage_stats', 'endpoint_summary', 'configuration', 'created_at', 'updated_at'];
    
    const updates: ProductUpdateData = {};
    
    Object.entries(productChanges).forEach(([key, value]) => {
      const field = key as keyof Product;
      if (!readOnlyFields.includes(field) && value !== undefined) {
        // Type assertion needed due to complex Product interface
        (updates as Record<string, unknown>)[field] = value;
      }
    });

    return updates;
  }, [productChanges]);

  const isFieldChanged = useCallback((field: keyof Product): boolean => {
    return field in productChanges;
  }, [productChanges]);

  const setSaving = useCallback((saving: boolean) => {
    setIsSaving(saving);
  }, []);

  return (
    <ProductEditContext.Provider
      value={{
        // Legacy API
        hasChanges,
        setHasChanges,
        markAsChanged,
        resetChanges,
        
        // New API
        productChanges,
        originalProduct,
        updateField,
        updateNestedField,
        getUpdatedProduct,
        isFieldChanged,
        isSaving,
        setSaving,
        initializeProduct,
      }}
    >
      {children}
    </ProductEditContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useProductEdit() {
  const context = useContext(ProductEditContext);
  if (context === undefined) {
    throw new Error('useProductEdit must be used within a ProductEditProvider');
  }
  return context;
}